/**
 * validarPayloadContraContrato — SSOT canónico de validación contra contrato.
 *
 * Función pura sin estado, agnóstica de entidad. Reemplaza las 4 copias
 * casi-idénticas que vivían en pozos-comunes-service.js, admin-nodos-crud-y-arbol.js,
 * admin-lotowheel-sorteos.js y admin-dispositivos-management.js (~300 líneas
 * → ~80 líneas + 4 wrappers de 5 líneas que solo arman el `consolidado`).
 *
 * @param {Object} consolidado — vista plana de la entidad (top-level + config
 *        spread). Cada caller lo arma según su shape (pozos es plano, nodo
 *        spread config bajo top-level, sorteo lotowheel idem, dispositivo
 *        idem). Soporta paths con puntos.
 * @param {Object} contrato — { nombreEntidad, campos: { 'path': { tipo, default,
 *        nullable?, aplicaSi?, opciones?, regex?, minInclusive?, maxInclusive?,
 *        maxLength?, maxItems?, maxKeys?, descripcion } } }
 *
 * Caps de tamaño (defense-in-depth contra DoS al disco):
 *   - maxLength (string): rechaza si valor.length > maxLength.
 *   - maxItems (array): rechaza si valor.length > maxItems.
 *   - maxKeys (objeto): rechaza si Object.keys(valor).length > maxKeys.
 * @returns {Array<{path, valorActual, motivo, descripcion}>} errores; vacío = OK.
 *
 * Reglas (idénticas a Borrador.validarContraContrato — mantener en sincronía):
 *   - Campos AUSENTES (undefined) se ignoran (deepMerge backend preservará).
 *   - Campos PRESENTES como `null`: válidos salvo `nullable: false` (rechaza
 *     con motivo 'no_puede_ser_nulo').
 *   - `aplicaSi(consolidado) === false` ignora el campo entero.
 *   - Tipos: 'entero', 'numero', 'booleano', 'string', 'array', 'objeto', 'enum'.
 *   - Rangos: minInclusive / maxInclusive en numero/entero.
 *   - Patrón: regex para string.
 *
 * Defense in depth: usado en backend post-frontend. El frontend valida con
 * el motor `Borrador.validarContraContrato` para UX inmediato; el backend
 * revalida con este helper porque cualquier cliente (script, CLI, Postman,
 * MCP) puede mandar inputs malos directamente.
 */
function leerValorEnPathConPuntos(objeto, path) {
    if (objeto === null || objeto === undefined) return undefined;
    const segs = path.split('.');
    let cur = objeto;
    for (let i = 0; i < segs.length; i++) {
        if (cur === null || cur === undefined || typeof cur !== 'object') return undefined;
        cur = cur[segs[i]];
    }
    return cur;
}

function escribirValorEnPathConPuntos(objeto, path, valor) {
    const segs = path.split('.');
    let cur = objeto;
    for (let i = 0; i < segs.length - 1; i++) {
        const seg = segs[i];
        if (cur[seg] === null || cur[seg] === undefined || typeof cur[seg] !== 'object' || Array.isArray(cur[seg])) {
            cur[seg] = {};
        }
        cur = cur[seg];
    }
    cur[segs[segs.length - 1]] = valor;
}

/**
 * Hidrata un objeto rellenando campos null/undefined con los `default`
 * declarados en el contrato. Muta el objeto IN-PLACE.
 *
 * Reglas:
 *   - Campos con `aplicaSi(consolidado) === false` → no se rellenan.
 *   - Campos sin `default` declarado → quedan null/undefined.
 *   - Campos con valor distinto de null/undefined → se preservan tal cual.
 *
 * Usado por:
 *   - Borrador.hidratarConDefaultsDelContrato (modal admin, antes de pintar
 *     inputs) — la copia de trabajo arranca completa y sana.
 *   - Backend GET con `?hidratar=true` (lotowheel-public-api.js) — el
 *     snapshot que viaja al modal admin sale auto-hidratado de DB legacy.
 */
function hidratarObjetoConDefaultsDelContrato(objeto, contrato) {
    if (!objeto || !contrato || !contrato.campos) return;
    const paths = Object.keys(contrato.campos);
    for (let i = 0; i < paths.length; i++) {
        const path = paths[i];
        const reglas = contrato.campos[path];
        if (typeof reglas.aplicaSi === 'function' && !reglas.aplicaSi(objeto)) continue;
        const valorActual = leerValorEnPathConPuntos(objeto, path);
        if ((valorActual === null || valorActual === undefined) && reglas.default !== undefined) {
            escribirValorEnPathConPuntos(objeto, path, reglas.default);
        }
    }
}

function validarPayloadContraContrato(consolidado, contrato) {
    const errores = [];
    if (!consolidado || !contrato || !contrato.campos) return errores;

    const paths = Object.keys(contrato.campos);
    for (let i = 0; i < paths.length; i++) {
        const path = paths[i];
        const reglas = contrato.campos[path];
        if (typeof reglas.aplicaSi === 'function' && !reglas.aplicaSi(consolidado)) continue;

        const valor = leerValorEnPathConPuntos(consolidado, path);

        // Ausente del payload → preservar (deepMerge). NO es error.
        if (valor === undefined) continue;

        // Presente como null → válido salvo `nullable: false`.
        if (valor === null) {
            if (reglas.nullable === false) {
                errores.push({
                    path,
                    valorActual: valor,
                    motivo: 'no_puede_ser_nulo',
                    descripcion: reglas.descripcion,
                });
            }
            continue;
        }

        if (reglas.ui?.formato === 'json') {
            const motivo = motivoJsonInvalido(valor, reglas.maxBytes);
            if (motivo) {
                errores.push({ path, valorActual: '[JSON]', motivo, descripcion: reglas.descripcion });
                continue;
            }
        }

        // Tipo.
        if (reglas.tipo === 'entero' && !Number.isInteger(valor)) {
            errores.push({ path, valorActual: valor, motivo: 'no_es_entero', descripcion: reglas.descripcion });
            continue;
        }
        if (reglas.tipo === 'numero' && (typeof valor !== 'number' || !Number.isFinite(valor))) {
            errores.push({ path, valorActual: valor, motivo: 'no_es_numero_finito', descripcion: reglas.descripcion });
            continue;
        }
        if (reglas.tipo === 'booleano' && typeof valor !== 'boolean') {
            errores.push({ path, valorActual: valor, motivo: 'no_es_booleano', descripcion: reglas.descripcion });
            continue;
        }
        if (reglas.tipo === 'string' && typeof valor !== 'string') {
            errores.push({ path, valorActual: valor, motivo: 'no_es_string', descripcion: reglas.descripcion });
            continue;
        }
        if (reglas.tipo === 'array' && !Array.isArray(valor)) {
            errores.push({ path, valorActual: valor, motivo: 'no_es_array', descripcion: reglas.descripcion });
            continue;
        }
        if (reglas.tipo === 'objeto' && (typeof valor !== 'object' || Array.isArray(valor))) {
            errores.push({ path, valorActual: valor, motivo: 'no_es_objeto', descripcion: reglas.descripcion });
            continue;
        }
        if (reglas.tipo === 'enum' && Array.isArray(reglas.opciones) && !reglas.opciones.includes(valor)) {
            errores.push({ path, valorActual: valor, motivo: 'fuera_de_opciones', descripcion: reglas.descripcion });
            continue;
        }

        // Rangos.
        if (reglas.minInclusive !== undefined && typeof valor === 'number' && valor < reglas.minInclusive) {
            errores.push({
                path,
                valorActual: valor,
                motivo: `menor_que_minimo_${reglas.minInclusive}`,
                descripcion: reglas.descripcion,
            });
        }
        if (reglas.maxInclusive !== undefined && typeof valor === 'number' && valor > reglas.maxInclusive) {
            errores.push({
                path,
                valorActual: valor,
                motivo: `mayor_que_maximo_${reglas.maxInclusive}`,
                descripcion: reglas.descripcion,
            });
        }

        // Patrón.
        if (reglas.regex && typeof valor === 'string' && !reglas.regex.test(valor)) {
            errores.push({ path, valorActual: valor, motivo: 'no_matchea_regex', descripcion: reglas.descripcion });
        }

        // Caps de tamaño — defense-in-depth contra DoS al disco con strings,
        // arrays u objetos gigantes. Rechazamos en el boundary antes de
        // persistir TEXT/BLOB de MB que rompen backups, queries, exports.
        if (reglas.maxLength !== undefined && typeof valor === 'string' && valor.length > reglas.maxLength) {
            errores.push({
                path,
                valorActual: `[string len ${valor.length}]`,
                motivo: `excede_max_length_${reglas.maxLength}`,
                descripcion: reglas.descripcion,
            });
        }
        if (reglas.maxItems !== undefined && Array.isArray(valor) && valor.length > reglas.maxItems) {
            errores.push({
                path,
                valorActual: `[array len ${valor.length}]`,
                motivo: `excede_max_items_${reglas.maxItems}`,
                descripcion: reglas.descripcion,
            });
        }
        if (
            reglas.maxKeys !== undefined &&
            valor !== null &&
            typeof valor === 'object' &&
            !Array.isArray(valor) &&
            Object.keys(valor).length > reglas.maxKeys
        ) {
            errores.push({
                path,
                valorActual: `[object keys ${Object.keys(valor).length}]`,
                motivo: `excede_max_keys_${reglas.maxKeys}`,
                descripcion: reglas.descripcion,
            });
        }
    }
    return errores;
}

// Datos JSON, no objetos ejecutables. El límite incluye todo el árbol.
function motivoJsonInvalido(valor, maxBytes = 65536, maxProfundidad = 16) {
    const pila = [[valor, 0]],
        vistos = new Set();
    let caracteres = 0;
    while (pila.length) {
        const [actual, profundidad, salida] = pila.pop();
        if (salida) {
            vistos.delete(actual);
            continue;
        }
        if (profundidad > maxProfundidad) return 'json_demasiado_profundo';
        if (actual === null || typeof actual === 'boolean') caracteres += 5;
        else if (typeof actual === 'number') {
            if (!Number.isFinite(actual)) return 'json_invalido';
            caracteres += String(actual).length;
        } else if (typeof actual === 'string') caracteres += actual.length + 2;
        else {
            if (
                typeof actual !== 'object' ||
                vistos.has(actual) ||
                (!Array.isArray(actual) && ![Object.prototype, null].includes(Object.getPrototypeOf(actual)))
            )
                return 'json_invalido';
            vistos.add(actual);
            pila.push([actual, profundidad, true]);
            caracteres += 2;
            for (const clave of Object.keys(actual)) {
                if (['__proto__', 'constructor', 'prototype'].includes(clave)) return 'json_invalido';
                caracteres += clave.length + 3;
                pila.push([actual[clave], profundidad + 1]);
            }
        }
        if (caracteres > maxBytes) return 'json_demasiado_grande';
    }
    return new TextEncoder().encode(JSON.stringify(valor)).length > maxBytes ? 'json_demasiado_grande' : null;
}

/**
 * Traduce un motivo técnico (`excede_max_length_500`, `no_es_entero`, etc.)
 * a un mensaje humano corto en español, listo para mostrar en toast/banner
 * al admin sin filtrar jerga interna. Si no reconoce el motivo, devuelve el
 * motivo crudo (mejor que nada).
 *
 * SSOT consumido por frontend (Borrador.validarContraContrato → toast del
 * modal) y backend si quisiera devolver mensajes ya traducidos.
 */
function motivoErrorAHumano(motivo) {
    if (typeof motivo !== 'string') return String(motivo || '');

    // Patrones con número embebido — se desarman para el mensaje.
    const matchMaxLen = motivo.match(/^excede_max_length_(\d+)$/);
    if (matchMaxLen) return `supera el largo máximo de ${matchMaxLen[1]} caracteres`;

    const matchMaxItems = motivo.match(/^excede_max_items_(\d+)$/);
    if (matchMaxItems) return `supera el máximo de ${matchMaxItems[1]} elementos`;

    const matchMaxKeys = motivo.match(/^excede_max_keys_(\d+)$/);
    if (matchMaxKeys) return `supera el máximo de ${matchMaxKeys[1]} campos`;

    const matchMin = motivo.match(/^menor_que_minimo_(.+)$/);
    if (matchMin) return `debe ser mayor o igual a ${matchMin[1]}`;

    const matchMax = motivo.match(/^mayor_que_maximo_(.+)$/);
    if (matchMax) return `debe ser menor o igual a ${matchMax[1]}`;

    const tabla = {
        no_puede_ser_nulo: 'no puede ser nulo',
        no_es_entero: 'debe ser un número entero',
        no_es_numero_finito: 'debe ser un número finito',
        no_es_booleano: 'debe ser true o false',
        no_es_string: 'debe ser texto',
        no_es_array: 'debe ser una lista',
        no_es_objeto: 'debe ser un objeto',
        fuera_de_opciones: 'no es una opción válida',
        no_matchea_regex: 'tiene un formato inválido',
        json_invalido: 'debe contener datos JSON válidos',
        json_demasiado_grande: 'supera el tamaño permitido',
        json_demasiado_profundo: 'tiene demasiados niveles anidados',
    };
    if (tabla[motivo]) return tabla[motivo];
    // Fallback: motivo desconocido — devolvemos el motivo crudo y avisamos
    // por consola para que en QA/dev el desarrollador agregue la traducción.
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
        console.warn('[motivoErrorAHumano] motivo sin traducción:', motivo);
    }
    return motivo;
}

if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
        validarPayloadContraContrato,
        hidratarObjetoConDefaultsDelContrato,
        motivoErrorAHumano,
        motivoJsonInvalido,
    };
}

export { validarPayloadContraContrato, hidratarObjetoConDefaultsDelContrato, motivoErrorAHumano, motivoJsonInvalido };
